import React from 'react';
import BlurFade from '../primitives/BlurFade';
import { Section } from '../layout/Section';
import { Container } from '../layout/Container';
import { Eyebrow, Headline, Body } from '../layout/typography';
import Pill from '../ui/Pill';
import CountUp from '../primitives/CountUp';
import { useLang } from '../../LangContext';
import { mediaAlt } from '../../lib/mediaSeo';

/**
 * AboutBlock — founder/brand story. Two columns on desktop, stacked mobile.
 * Optional stats list rendered inline with CountUp.
 */
export default function AboutBlock({
  id, testId, tone = 'cream', rhythm = 'grand',
  eyebrow, title, paragraphs = [], mediaSrc, stats = [], cta,
}) {
  const { lang, overlay } = useLang();
  return (
    <Section id={id} data-testid={testId} tone={tone} rhythm={rhythm}>
      <Container className="grid grid-cols-1 lg:grid-cols-12 gap-y-12 lg:gap-x-16 items-start">
        <BlurFade className="lg:col-span-5 lg:col-start-1">
          {mediaSrc && (
            <div className="relative rounded-[20px] overflow-hidden aspect-[4/5] bg-white">
              <img src={mediaSrc} alt={mediaAlt(overlay, mediaSrc, lang, title || eyebrow || '')} className="absolute inset-0 w-full h-full object-cover" />
            </div>
          )}
        </BlurFade>

        <div className="lg:col-span-6 lg:col-start-7">
          {eyebrow && <BlurFade><Eyebrow className="mb-7">{eyebrow}</Eyebrow></BlurFade>}
          {title && <BlurFade delay={0.05}><Headline size="lg">{title}</Headline></BlurFade>}

          <div className="mt-7 space-y-5 max-w-xl">
            {paragraphs.map((p, i) => (
              <BlurFade key={`p-${i}-${String(p).slice(0, 20)}`} delay={0.08 * (i + 1)}>
                <Body>{p}</Body>
              </BlurFade>
            ))}
          </div>

          {stats.length > 0 && (
            <BlurFade delay={0.4} className="mt-12 grid grid-cols-3 gap-4 max-w-xl">
              {stats.map((s) => {
                const m = String(s.n).match(/^(\d+)(.*)$/);
                return (
                  <div key={`stat-${s.l}`} className="border-l border-[#F26B1A]/30 pl-4">
                    <p className="font-display text-[32px] lg:text-[40px] leading-none text-[#1F1D1A]">
                      {m ? <CountUp to={parseInt(m[1], 10)} suffix={m[2]} duration={1.6} /> : s.n}
                    </p>
                    <p className="text-[#5C5852] text-[11px] uppercase tracking-[0.15em] mt-2">{s.l}</p>
                  </div>
                );
              })}
            </BlurFade>
          )}

          {cta && (
            <BlurFade delay={0.5} className="mt-10 inline-block">
              <Pill variant="outline" href={cta.href} testId={cta.testId} source={cta.source}>
                {cta.label}
              </Pill>
            </BlurFade>
          )}
        </div>
      </Container>
    </Section>
  );
}
